import generateNumberVariations from "@/components/FpAppV4/services/generateNumberVariations";
import store from "../../../redux/store";
import {
  ReferenceType,
  FpAppV4DataType,
  QueryAnswerType,
  SourceMapType,
} from "../../FpAppV4/utils/fpAppV4Interface";
import fpFetch from "@/services/fpFetch";
import { BASE_API_URL } from "@/constants";
import { updateFpAppV4Data } from "@/redux/fpAppV4Slice";
import { fetchWorkspace } from "@/components/FpEditor/services/useWorkspaceData";

export const getSourceMapForQid = async (qid: string) => {
  let sourceMap: SourceMapType | undefined = undefined;
  const fpAppV4Data = store.getState().fpAppV4Data as FpAppV4DataType;
  const queryAnswer = (fpAppV4Data?.queryAnswers || []).find(
    (qa: QueryAnswerType) => qa.qid === qid
  );
  sourceMap = queryAnswer?.toolResponse?.answer?.sourceMap;
  const backFilledSourceMap = queryAnswer?.backFilledSourceMap;
  const isTest = window.location.href.includes("/test-highlight");

  if (!sourceMap || !backFilledSourceMap) {
    if (!isTest) {
      const response = await fpFetch(
        `${BASE_API_URL}/queryResults/getQueryAnswerSourceMap?id=${qid}`
      );
      const data = await response.json();
      sourceMap = data.sourceMap;
    } else {
      if (!sourceMap) {
        console.error("Source map missing for Test.");
      }

      sourceMap = JSON.parse(JSON.stringify(sourceMap));
    }

    for (const key in sourceMap) {
      const refs = sourceMap[key];
      for (const [i, ref] of refs.entries()) {
        if (ref.parentSrcNum) {
          sourceMap[key][i] = {
            ...ref,
            ...(sourceMap[ref.parentSrcNum] || [{}])[0],
          };
        }
      }
    }

    // Ensure Redux has this queryAnswer entry; if not, fetch from workspace and insert
    const queryAnswers = JSON.parse(
      JSON.stringify(fpAppV4Data?.queryAnswers || [])
    ) as QueryAnswerType[];
    let index = queryAnswers.findIndex((qa) => qa.qid === qid);

    if (index === -1) {
      try {
        // Try to determine workspaceId from Redux or URL
        const workspaceIdFromRedux = (fpAppV4Data as any)?.workspaceId;
        const workspaceIdFromPath = extractWorkspaceIdFromPath(
          window.location.pathname
        );
        const workspaceId = workspaceIdFromRedux || workspaceIdFromPath || "";

        if (workspaceId) {
          const ws = await fetchWorkspace(workspaceId);
          const messagePairs = ((ws as any)?.message_pairs as any[]) || [];
          const queryData = messagePairs.find((mp) => mp._id === qid);
          if (queryData) {
            const mapped: any = {
              toolResponse: { ...queryData },
              qid: queryData._id,
              added: true,
              tableData: queryData.tableData,
              status: queryData.status,
              toolId: queryData.agent,
              query: queryData.query,
              company: queryData.company,
            };
            // Attach sourceMap we just fetched
            mapped.toolResponse.answer = {
              ...(mapped.toolResponse.answer || {}),
              sourceMap: sourceMap || {},
            };
            (mapped as any).backFilledSourceMap = true;
            queryAnswers.push(mapped);
            index = queryAnswers.length - 1;
          } else {
            // Fallback: insert minimal entry so show work can proceed
            queryAnswers.push({
              qid,
              toolResponse: { answer: { sourceMap: sourceMap || {} } },
              added: true,
              status: "completed",
            } as any);
            index = queryAnswers.length - 1;
          }
        } else {
          // No workspaceId available; still insert minimal entry
          queryAnswers.push({
            qid,
            toolResponse: { answer: { sourceMap: sourceMap || {} } },
            added: true,
            status: "completed",
          } as any);
          index = queryAnswers.length - 1;
        }
      } catch (e) {
        console.warn("[getSourceMapForQid] failed to backfill queryAnswer", e);
      }
    } else {
      // Update existing entry's sourceMap
      (queryAnswers[index] as any).toolResponse = {
        ...((queryAnswers[index] as any).toolResponse || {}),
        answer: {
          ...(((queryAnswers[index] as any).toolResponse || {}).answer || {}),
          sourceMap: sourceMap || {},
        },
      };
      (queryAnswers[index] as any).backFilledSourceMap = true;
    }

    store.dispatch(updateFpAppV4Data({ queryAnswers }));
  }

  return sourceMap || {};
};

// Helper: extract workspace id from URL path
function extractWorkspaceIdFromPath(pathname: string): string | null {
  const patterns = [
    /^\/reports\/([a-f0-9-]{36})$/,
    /^\/chat\/([a-f0-9-]{36})$/,
    /^\/matrix\/([a-f0-9-]{36})$/,
  ];
  for (const pattern of patterns) {
    const match = pathname.match(pattern);
    if (match) return match[1];
  }
  return null;
}

export const getQueryAnswerUsedDocuments = async (qid: string) => {
  const response = await fpFetch(
    `${BASE_API_URL}/queryResults/getQueryAnswerUsedDocuments?id=${qid}`
  );
  const data = await response.json();
  return data;
};

const getSourceRefsImpl = async (
  qid: string,
  numValue: string,
  numValueWithinSrc?: string
): Promise<ReferenceType[]> => {
  let sourceMap: SourceMapType = {};
  sourceMap = await getSourceMapForQid(qid);

  let ref = sourceMap[numValue];

  if (!ref) {
    const numValueVariations = generateNumberVariations(numValue);
    for (const numValueVariation of numValueVariations) {
      ref = sourceMap[numValueVariation];
      if (ref) {
        break;
      }
    }
  }

  if (ref) {
    const uniqueRef = ref.filter(
      (item, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            ("interactionId" in t ? t.interactionId : null) ===
              ("interactionId" in item ? item.interactionId : null) &&
            t.docId === item.docId &&
            t.pageNum === item.pageNum
        )
    );

    // If we've clicked a number and actually want to use the source listed at the end of the bullet point,
    // we are going to use that source's ref here but inject data so that we highlight the number we're looking for.
    if (numValueWithinSrc) {
      const poisonedRefs = uniqueRef.map((r) => ({
        ...r,
        originalNumValue: numValueWithinSrc, // Number(numValueWithinSrc.replace(/\D/g, "")),
        /*
          isOpen: true,
          selectedDocument: ref.parentDoc,
          highlightContext: ref.smallContext || ref.content,
          highlightPageNum: ref.pageNum,
          originalNumValue:
            metaDetails?.originalNumValue || refDetails.originalNumValue,
          parentElementText:
            metaDetails?.parentElementText || refDetails.parentElementText,
        */
      }));
      // console.log("Poisoning refs", poisonedRefs);
      return poisonedRefs;
    } else {
      // console.log("Returning refs", uniqueRef);
    }

    return uniqueRef;
  }
  return [];
};

export const getSourceRefs = async (
  qid: string,
  numValue: string,
  numValueWithinSrc?: string
) => {
  return await getSourceRefsImpl(qid, numValue, numValueWithinSrc);
};
